import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import PhygitalIcon from "@/components/PhygitalIcon";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Calendar as CalendarIcon,
  Heart,
  Camera,
  Sparkles,
  Users,
  Sun,
  Star,
  Check,
  Plus,
  Scissors,
  Eye,
  Palette,
  X,
  Clock,
  Trash2
} from "lucide-react";
import { toast } from "sonner";

// Event types with recommended services
const EVENT_TYPES = [
  { 
    id: "wedding", 
    name: "Wedding", 
    icon: Heart, 
    color: "bg-pink-100 text-pink-600",
    services: ["Hair", "Makeup", "Nails", "Lashes"],
    timeline: "Book 2-3 months ahead, trial 1 month before"
  },
  { 
    id: "photoshoot", 
    name: "Photoshoot", 
    icon: Camera, 
    color: "bg-purple-100 text-purple-600",
    services: ["Hair", "Makeup", "Lashes"],
    timeline: "Book 2-4 weeks ahead"
  },
  { 
    id: "graduation", 
    name: "Graduation", 
    icon: Star, 
    color: "bg-amber-100 text-amber-600",
    services: ["Hair", "Nails", "Makeup"],
    timeline: "Book 1-2 weeks ahead"
  },
  { 
    id: "birthday", 
    name: "Birthday/Party", 
    icon: Sparkles, 
    color: "bg-blue-100 text-blue-600",
    services: ["Hair", "Nails", "Makeup", "Lashes"],
    timeline: "Book 1-2 weeks ahead"
  },
  { 
    id: "date_night", 
    name: "Date Night", 
    icon: Heart, 
    color: "bg-red-100 text-red-600",
    services: ["Hair", "Nails", "Lashes"],
    timeline: "Book 3-5 days ahead"
  },
  { 
    id: "vacation", 
    name: "Vacation", 
    icon: Sun, 
    color: "bg-teal-100 text-teal-600",
    services: ["Nails", "Hair", "Lashes"],
    timeline: "Book 1 week ahead for low-maintenance styles"
  },
  { 
    id: "interview", 
    name: "Interview/Work Event", 
    icon: Users, 
    color: "bg-gray-100 text-gray-700",
    services: ["Hair", "Nails"],
    timeline: "Book 2-3 days ahead for polished look"
  },
  { 
    id: "maintenance", 
    name: "General Maintenance", 
    icon: Sparkles, 
    color: "bg-green-100 text-green-600",
    services: ["Hair", "Nails", "Lashes"],
    timeline: "Regular self-care routine"
  }
];

// Service icons
const SERVICE_ICONS = {
  "Hair": Scissors,
  "Nails": Sparkles,
  "Makeup": Palette,
  "Lashes": Eye
};

const PlanExperience = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [showEventDetail, setShowEventDetail] = useState(null);
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [selectedServices, setSelectedServices] = useState([]);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/user/events`, { withCredentials: true });
      setEvents(res.data || []);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    const dateStr = date.toISOString().split('T')[0];
    setEventDate(dateStr);
    
    // Check if there's an event on this date
    const eventOnDate = events.find(e => e.date === dateStr);
    if (eventOnDate) {
      setShowEventDetail(eventOnDate);
    } else {
      setShowAddEvent(true);
    }
  };

  const handleSelectEventType = (eventType) => {
    setSelectedEventType(eventType);
    setSelectedServices(eventType.services);
    if (!eventName && eventType.id !== "maintenance") {
      setEventName(eventType.name);
    }
  };

  const toggleService = (service) => {
    setSelectedServices(prev => 
      prev.includes(service) 
        ? prev.filter(s => s !== service)
        : [...prev, service]
    );
  };

  const handleCreateEvent = async () => {
    if (!selectedEventType || !eventDate) {
      toast.error("Please select event type and date");
      return;
    }

    try {
      // Calculate scheduled service dates based on event type
      const eventDateObj = new Date(eventDate);
      const scheduledServices = selectedServices.map(service => {
        const daysBeforeMap = {
          "Hair": selectedEventType.id === "wedding" ? 30 : 7,
          "Makeup": selectedEventType.id === "wedding" ? 30 : 1,
          "Nails": 3,
          "Lashes": 5
        };
        const daysBefore = daysBeforeMap[service] || 7;
        const serviceDate = new Date(eventDateObj);
        serviceDate.setDate(serviceDate.getDate() - daysBefore);
        
        return {
          service,
          suggested_date: serviceDate.toISOString().split('T')[0],
          scheduled_date: null,
          booked: false
        };
      });

      const res = await axios.post(`${API}/user/events`, {
        type: selectedEventType.id,
        name: eventName || selectedEventType.name,
        date: eventDate,
        linkedServices: selectedServices,
        scheduled_services: scheduledServices
      }, { withCredentials: true });

      toast.success("Experience created!");
      setShowAddEvent(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error("Could not create experience");
    }
  };

  const handleUpdateServiceDate = async (event, serviceIndex, newDate) => {
    try {
      const updatedServices = [...event.scheduled_services];
      updatedServices[serviceIndex] = {
        ...updatedServices[serviceIndex],
        scheduled_date: newDate
      };

      await axios.put(`${API}/user/events/${event.event_id}`, {
        scheduled_services: updatedServices
      }, { withCredentials: true });

      toast.success("Service date updated!");
      fetchEvents();
      // Update local state
      setShowEventDetail({
        ...event,
        scheduled_services: updatedServices
      });
    } catch (error) {
      toast.error("Could not update date");
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await axios.delete(`${API}/user/events/${eventId}`, { withCredentials: true });
      toast.success("Experience deleted");
      setShowEventDetail(null);
      fetchEvents();
    } catch (error) {
      toast.error("Could not delete experience");
    }
  };

  const resetForm = () => {
    setSelectedEventType(null);
    setEventName("");
    setEventDate("");
    setSelectedServices([]);
    setSelectedDate(null);
  };

  // Get dates that have events for calendar highlighting
  const eventDates = events.map(e => new Date(e.date + "T00:00:00"));
  
  // Get dates for scheduled services too
  const serviceDates = events.flatMap(e => 
    (e.scheduled_services || [])
      .filter(s => s.scheduled_date || s.suggested_date)
      .map(s => new Date((s.scheduled_date || s.suggested_date) + "T00:00:00"))
  );

  const getEventTypeById = (typeId) => EVENT_TYPES.find(t => t.id === typeId);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="p-2 -ml-2 hover:bg-gray-100 rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-semibold text-base">My Experiences</h1>
              <p className="text-xs text-gray-600">Plan your beauty appointments</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* Calendar */}
        <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            className="w-full"
            modifiers={{
              hasEvent: eventDates,
              hasService: serviceDates
            }}
            modifiersStyles={{
              hasEvent: { 
                backgroundColor: '#fef3c7', 
                color: '#92400e',
                fontWeight: 'bold'
              },
              hasService: { 
                backgroundColor: '#ede9fe', 
                color: '#7c3aed'
              }
            }}
          />
          <div className="px-4 pb-4 flex items-center gap-4 text-xs text-gray-600">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-amber-100 border border-amber-300" />
              <span>Event</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-violet-100 border border-violet-300" />
              <span>Service</span>
            </div>
          </div>
        </section>

        {/* Upcoming Experiences */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-sm text-gray-600 uppercase tracking-wide">Upcoming</h2>
            <button 
              onClick={() => setShowAddEvent(true)}
              className="flex items-center gap-1 text-xs text-gray-700 hover:text-gray-900"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>
          
          {events.length > 0 ? (
            <div className="space-y-3">
              {events
                .filter(e => new Date(e.date) >= new Date(new Date().toDateString()))
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .map((event) => {
                  const eventType = getEventTypeById(event.type);
                  const IconComponent = eventType?.icon || CalendarIcon;
                  
                  return (
                    <div 
                      key={event.event_id}
                      onClick={() => setShowEventDetail(event)}
                      className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg ${eventType?.color || 'bg-gray-100 text-gray-700'} flex items-center justify-center`}>
                          <IconComponent className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="font-medium text-sm">{event.name}</h3>
                            <Badge variant="outline" className="text-xs">
                              {new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {(event.linkedServices || []).map((service, idx) => {
                              const ServiceIcon = SERVICE_ICONS[service] || Sparkles;
                              const scheduledService = (event.scheduled_services || []).find(s => s.service === service);
                              const hasDate = scheduledService?.scheduled_date;
                              
                              return (
                                <Badge 
                                  key={idx} 
                                  variant={hasDate ? "default" : "secondary"}
                                  className="text-xs"
                                >
                                  <ServiceIcon className="w-3 h-3 mr-1" />
                                  {service}
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
              <CalendarIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-600">No upcoming experiences</p>
              <p className="text-xs text-gray-500 mt-1">Tap a date to plan something</p>
            </div>
          )}
        </section>

        {/* Past Experiences */}
        {events.filter(e => new Date(e.date) < new Date(new Date().toDateString())).length > 0 && (
          <section>
            <h2 className="font-semibold text-sm text-gray-600 uppercase tracking-wide mb-3">Past</h2>
            <div className="space-y-2">
              {events
                .filter(e => new Date(e.date) < new Date(new Date().toDateString()))
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .slice(0, 3)
                .map((event) => {
                  const eventType = getEventTypeById(event.type);
                  const IconComponent = eventType?.icon || CalendarIcon;
                  
                  return (
                    <div 
                      key={event.event_id}
                      onClick={() => setShowEventDetail(event)}
                      className="bg-gray-50 rounded-lg border border-gray-100 p-3 cursor-pointer hover:bg-gray-100 transition-colors opacity-75"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg ${eventType?.color || 'bg-gray-100 text-gray-700'} flex items-center justify-center`}>
                          <IconComponent className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-sm">{event.name}</h3>
                          <p className="text-xs text-gray-600">
                            {new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </section>
        )}
      </main>

      {/* Add Experience Modal */}
      <Dialog open={showAddEvent} onOpenChange={(open) => { setShowAddEvent(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Plan an Experience</DialogTitle>
            <DialogDescription>
              {selectedDate 
                ? `For ${selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`
                : "What are you planning?"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Event Type Selection */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Type of Experience</label>
              <div className="grid grid-cols-2 gap-2">
                {EVENT_TYPES.map((eventType) => {
                  const IconComponent = eventType.icon;
                  const isSelected = selectedEventType?.id === eventType.id;
                  
                  return (
                    <button
                      key={eventType.id}
                      onClick={() => handleSelectEventType(eventType)}
                      className={`p-3 rounded-xl border text-left transition-all relative ${
                        isSelected 
                          ? 'border-gray-900 bg-gray-50 shadow-sm' 
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg ${eventType.color} flex items-center justify-center mb-1`}>
                        <IconComponent className="w-4 h-4" />
                      </div>
                      <p className="font-medium text-xs">{eventType.name}</p>
                      {isSelected && (
                        <Check className="w-4 h-4 text-green-500 absolute top-2 right-2" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedEventType && (
              <>
                {/* Event Name */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Name (optional)</label>
                  <Input
                    placeholder={selectedEventType.name}
                    value={eventName}
                    onChange={(e) => setEventName(e.target.value)}
                  />
                </div>

                {/* Event Date */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Date</label>
                  <Input
                    type="date"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                  />
                </div>

                {/* Services */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Services</label>
                  <div className="flex flex-wrap gap-2">
                    {["Hair", "Nails", "Makeup", "Lashes"].map((service) => {
                      const ServiceIcon = SERVICE_ICONS[service];
                      return (
                        <button
                          key={service}
                          onClick={() => toggleService(service)}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                            selectedServices.includes(service)
                              ? 'bg-gray-900 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <ServiceIcon className="w-4 h-4" />
                          {service}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Timeline Tip */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <p className="text-xs text-amber-700">
                    💡 {selectedEventType.timeline}
                  </p>
                </div>

                <Button 
                  onClick={handleCreateEvent}
                  className="w-full bg-black hover:bg-gray-800"
                  disabled={!eventDate}
                >
                  Create Experience
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Event Detail Modal */}
      <Dialog open={!!showEventDetail} onOpenChange={() => setShowEventDetail(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          {showEventDetail && (() => {
            const eventType = getEventTypeById(showEventDetail.type);
            const IconComponent = eventType?.icon || CalendarIcon;
            
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg ${eventType?.color || 'bg-gray-100'} flex items-center justify-center`}>
                      <IconComponent className="w-4 h-4" />
                    </div>
                    {showEventDetail.name}
                  </DialogTitle>
                  <DialogDescription>
                    {new Date(showEventDetail.date).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      month: 'long', 
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-2">
                  {/* Scheduled Services */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Service Timeline</h3>
                    <div className="space-y-2">
                      {(showEventDetail.scheduled_services || showEventDetail.linkedServices?.map(s => ({ service: s, suggested_date: null })) || []).map((scheduled, idx) => {
                        const ServiceIcon = SERVICE_ICONS[scheduled.service] || Sparkles;
                        const displayDate = scheduled.scheduled_date || scheduled.suggested_date;
                        
                        return (
                          <div key={idx} className="bg-gray-50 rounded-xl p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <ServiceIcon className="w-4 h-4 text-gray-700" />
                                <span className="font-medium text-sm">{scheduled.service}</span>
                              </div>
                              {scheduled.booked && (
                                <Badge className="bg-green-100 text-green-700 text-xs">Booked</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-gray-500" />
                              <Input
                                type="date"
                                value={scheduled.scheduled_date || scheduled.suggested_date || ""}
                                onChange={(e) => handleUpdateServiceDate(showEventDetail, idx, e.target.value)}
                                className="flex-1 h-8 text-sm"
                              />
                            </div>
                            {!scheduled.scheduled_date && scheduled.suggested_date && (
                              <p className="text-xs text-gray-600 mt-1">Suggested date</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Timeline visualization */}
                  {eventType && (
                    <div className="bg-violet-50 border border-violet-200 rounded-xl p-3">
                      <p className="text-xs text-violet-700">
                        💡 {eventType.timeline}
                      </p>
                    </div>
                  )}

                  {/* Delete button */}
                  <button
                    onClick={() => handleDeleteEvent(showEventDetail.event_id)}
                    className="flex items-center justify-center gap-2 w-full py-2 text-red-600 hover:text-red-700 text-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Experience
                  </button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 py-3">
        <div className="flex items-center justify-center gap-1 text-gray-500 text-xs">
          <PhygitalIcon className="w-4 h-4" showBox={false} />
          <span>phygital</span>
        </div>
      </footer>
    </div>
  );
};

export default PlanExperience;
